import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { RootState } from 'app/AppStore';
import { BamMotrioIcon } from 'assets/icons';
import { useFetchBamCommercialFamilyCodes } from 'domains/bamCommercialFamilyCodes/BamCommercialFamilyCodes.requests';
import { IAM, MOTRIO } from 'domains/catalog/Catalog.types';
import { getIAMReference } from 'domains/references';
import { isReferenceExadis } from 'domains/references/References.utils';
import { getTokenUser } from 'domains/user';
import { theme } from 'styles';
import { Box, Flex, Icon, MarginBox } from 'UI';
import { getData } from 'utils';

interface BamLogoProps {
  iconSize?: number;
  boxWidth?: number;
  boxHeight?: number;
  boxOffsetRight?: number;
  boxOffsetBottom?: number;
  logoOffsetTop?: number;
}

const SBox = styled(Box)`
  overflow: hidden;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  box-shadow: 0px 4px 10px 0px ${theme.color.grey75};
`;

const BamLogo = ({
  iconSize,
  boxWidth,
  boxHeight,
  boxOffsetRight,
  boxOffsetBottom,
  logoOffsetTop = -5,
}: BamLogoProps) => {
  return (
    <Flex justify={'flex-end'} align={'center'} size={0}>
      <MarginBox mb={boxOffsetBottom} mr={boxOffsetRight}>
        <SBox data-cy={'bam-logo'}>
          <Box background={theme.color.bam_logo} height={boxHeight} width={boxWidth}>
            <MarginBox mt={logoOffsetTop}>
              <Flex justify={'center'} size={boxWidth}>
                <Icon IconComponent={BamMotrioIcon} size={iconSize} color={theme.color.brand_black} />
              </Flex>
            </MarginBox>
          </Box>
        </SBox>
      </MarginBox>
    </Flex>
  );
};

interface BamCommercialFamilyLogoProps extends BamLogoProps {
  referenceNumber?: string;
  vehicleKey?: string;
}

const BamCommercialFamilyLogo = ({
  referenceNumber,
  vehicleKey,
  iconSize,
  boxWidth,
  boxHeight,
  boxOffsetBottom,
  boxOffsetRight,
  logoOffsetTop,
}: BamCommercialFamilyLogoProps) => {
  const bamCommercialFamilyCodes = useFetchBamCommercialFamilyCodes();
  const referenceRaw = useSelector((state: RootState) =>
    getIAMReference(state, {
      referenceNumber,
      vehicleKey,
    }),
  );

  const reference = getData(referenceRaw);

  const isMotrioBamReference = () =>
    reference?.supplier === MOTRIO &&
    !!reference.commercialFamily &&
    bamCommercialFamilyCodes.includes(reference.commercialFamily);

  const tokenUser = useSelector(getTokenUser);

  const showBamLogo =
    (isReferenceExadis(IAM, reference?.supplier, reference?.origin) || isMotrioBamReference()) &&
    tokenUser?.country === 'FR';

  return (
    showBamLogo && (
      <BamLogo
        iconSize={iconSize}
        boxWidth={boxWidth}
        boxHeight={boxHeight}
        boxOffsetBottom={boxOffsetBottom}
        boxOffsetRight={boxOffsetRight}
        logoOffsetTop={logoOffsetTop}
      />
    )
  );
};

export default BamCommercialFamilyLogo;
